import { FC, useContext, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { TELENOR_API_URL } from "../../../env";
import AppContext from "../../../contexts/AppContext";

type ReportsDataModel = {
  sla: SlaDataModel;
  automation: AutomationDegreeModel;
};

type SlaDataModel = {
  from: string;
  to: string;
  sla: {
    antall: number;
    sla: number;
    total: number;
  };
  groups: GroupSlaDataModel[];
};

type GroupSlaDataModel = {
  group_id: string;
  group_name: string;
  antall: number;
  sla: number;
  total: number;
};

type AutomationDegreeModel = {
  groups: GroupAutomationDataModel[];
  totals: {
    degreeOfAutomation: number;
    expectedSecTotal: number;
    savedSecTotal: number;
  };
};

type GroupAutomationDataModel = {
  name: string;
  group_id: string;
  original: string;
  current: string;
  automated_time: string;
  degreeOfAutomation: number;
};

const apiUrl = TELENOR_API_URL;
// console.log("Using Telenor API URL:", apiUrl);
// const apiUrl = "https://europe-west1-slog-prod.cloudfunctions.net/telenor-lightweight-api";

async function fetchReports(
  fromDate: string,
  toDate: string,
  userId?: string,
): Promise<ReportsDataModel> {
  const url = `${apiUrl}/flows/reports`;
  const headers = new Headers();
  if (userId) {
    headers.append("Auth0-User", userId);
  }
  const res = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify({
      from: `${fromDate} 00:00:00`,
      to: `${toDate} 23:59:59`,
    }),
  });
  const data = await res.json();
  console.log("Reports data fetched from API:", data);
  return data as ReportsDataModel;
}

export const ReportsPanel: FC<{ fromDate: string; toDate: string }> = ({
  fromDate,
  toDate,
}) => {
  const appContext = useContext(AppContext);
  const [reportsData, setReportsData] = useState<ReportsDataModel>();

  useEffect(() => {
    (async () => {
      const data = await fetchReports(fromDate, toDate, appContext.login?.uid);
      console.log("Fetched reports data:", data);
      data.sla.groups.sort((a, b) =>
        a.group_name.localeCompare(b.group_name, "en"),
      );
      data.automation.groups.sort((a, b) => a.name.localeCompare(b.name, "en"));
      setReportsData(data);
    })();
  }, [fromDate, toDate]);

  return reportsData ? (
    <div className="flex flex-1 gap-4 p-2">
      <div className="flex flex-1 flex-col h-72 p-2 bg-gray-200 rounded-2xl text-black p-4">
        <SlaByFlow data={reportsData.sla} />
      </div>
      <div className="flex flex-1 flex-col h-72 p-2 bg-gray-200 rounded-2xl text-black p-4">
        <AutomationByFlow data={reportsData.automation} />
      </div>
    </div>
  ) : null;
};

const SlaByFlow: FC<{ data: SlaDataModel }> = (props) => {
  const [selectedGroup, setSelectedGroup] = useState<GroupSlaDataModel>();

  const showSlaByFlow = false;

  const slaData = props.data;
  return slaData ? (
    <>
      <h3 className="text-xl font-bold">SLA</h3>
      <div className="flex flex-1 p-2">
        <div className="flex flex-1 flex-col items-center">
          <div className="flex h-8">
            <p className="m-auto">Total</p>
          </div>
          <div className="flex flex-1">
            <ResponsiveContainer width={100}>
              <BarChart
                data={[slaData.sla]}
                layout="horizontal"
                margin={{ top: 20, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <YAxis
                  type="number"
                  domain={[0, 100]}
                  tickFormatter={(val) => `${val}%`}
                />
                <XAxis type="category" dataKey="name" />
                <Tooltip formatter={(value: number) => `${value}%`} />
                <Bar dataKey="total" stackId="a" fill="#82ca9d" />
                {/* <Bar dataKey="diff" stackId="a" fill="red" /> */}
              </BarChart>
            </ResponsiveContainer>
            <p className="my-auto">{`${slaData?.sla?.sla}/${slaData?.sla?.antall}`}</p>
          </div>
        </div>
        {showSlaByFlow ? (
          <div className="flex flex-1 flex-col items-center">
            <div className="flex h-8">
              <select
                className="m-auto border-2 border-black"
                value={selectedGroup?.group_id}
                onChange={(e) =>
                  setSelectedGroup(
                    slaData?.groups.find(
                      (group) => group.group_id === e.target.value,
                    ),
                  )
                }
              >
                {slaData?.groups.map((group) => (
                  <option value={group.group_id}>{group.group_name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-1 gap-8">
              <ResponsiveContainer width={100} className="">
                <BarChart
                  data={[selectedGroup]}
                  layout="horizontal"
                  margin={{ top: 20, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <YAxis
                    type="number"
                    domain={[0, 100]}
                    tickFormatter={(val) => `${val}%`}
                  />
                  <XAxis type="category" dataKey="name" />
                  <Tooltip formatter={(value: number) => `${value}%`} />
                  <Bar dataKey="total" stackId="a" fill="#82ca9d" />
                  {/* <Bar dataKey="diff" stackId="a" fill="red" /> */}
                </BarChart>
              </ResponsiveContainer>
              <p className="my-auto">{`${selectedGroup?.sla}/${selectedGroup?.antall}`}</p>
            </div>
          </div>
        ) : null}
      </div>
    </>
  ) : null;
};

function formatSecondsToHMS(seconds: number): string {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
}

const AutomationByFlow: FC<{ data: AutomationDegreeModel }> = (props) => {
  const [selectedGroup, setSelectedGroup] =
    useState<GroupAutomationDataModel>();
  const automationData = props.data;
  return (
    <div className="flex flex-1 flex-col gap-2">
      <h3 className="font-bold">Automasjon</h3>
      <div>
        <h3 className="font-bold">Totaler</h3>
        <ul className="flex flex-1 flex-col pl-4">
          <li className="flex">
            <span className="w-48">Opprinnelig varighet:</span>
            {formatSecondsToHMS(props.data.totals.expectedSecTotal) || "-"}
          </li>
          <li className="flex">
            <span className="w-48">Spart tid:</span>
            {formatSecondsToHMS(props.data.totals.savedSecTotal) || "-"}
          </li>
          <li className="flex">
            <span className="w-48">Automatiseringsgrad:</span>
            {props.data.totals.degreeOfAutomation?.toFixed(2) || "-"}%
          </li>
        </ul>
      </div>
      <div>
        <div className="flex items-center gap-4">
          <span>Per sak:</span>
          <select
            className="border-2 border-black"
            value={selectedGroup?.group_id}
            onChange={(e) =>
              setSelectedGroup(
                automationData?.groups.find(
                  (group) => group.group_id === e.target.value,
                ),
              )
            }
          >
            {automationData?.groups.map((group) => (
              <option value={group.group_id}>{group.name}</option>
            ))}
          </select>
        </div>
        <ul className="flex flex-1 flex-col px-4">
          <li className="flex">
            <span className="w-48">Nåværende varighet:</span>{" "}
            {selectedGroup?.current || "-"}
          </li>
          <li className="flex">
            <span className="w-48">Opprinnelig varighet:</span>{" "}
            {selectedGroup?.original || "-"}
          </li>
          <li className="flex">
            <span className="w-48">Automatisert varighet:</span>{" "}
            {selectedGroup?.automated_time || "-"}
          </li>
          <li className="flex">
            <span className="w-48">Automatiseringsgrad:</span>
            {selectedGroup?.degreeOfAutomation?.toFixed(2) || "-"}%
          </li>
        </ul>
      </div>
    </div>
  );
};
